import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Radar, Sparkles, Lock } from "lucide-react";
import { http } from "@/lib/api";
import { useApp } from "@/lib/store";

export default function DriftRadar({ className = "" }) {
  const nav = useNavigate();
  const { deviceId } = useApp();
  const [r, setR] = useState(null);

  useEffect(() => {
    let ok = true;
    http.get(`/radar/${deviceId}`).then(({ data }) => ok && setR(data)).catch(() => {});
    return () => { ok = false; };
  }, [deviceId]);

  if (!r || !r.signal) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
      className={`ss-card p-5 border-l-2 border-[#C9A227] ${className}`}
      data-testid="drift-radar"
    >
      <div className="flex items-center gap-2">
        <Radar size={16} strokeWidth={1.8} className="text-[#C9A227]" />
        <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[#8a6d10]">Drift Radar</span>
        {r.level === "sustained" && (
          <span className="ml-auto text-[10.5px] font-medium rounded-full bg-[#F7F2D7] px-2 py-0.5 text-[#8a6d10]">
            holding
          </span>
        )}
      </div>

      {r.stage && (
        <div className="mt-1 text-[10.5px] uppercase tracking-[0.12em] text-[#9a9a94]" data-testid="radar-stage">
          {r.stage.label}
        </div>
      )}

      {r.locked ? (
        <>
          <p className="mt-3 text-[14.5px] font-medium text-[#1A1A1A]" data-testid="radar-headline">{r.headline}</p>
          <div className="mt-3 rounded-xl border border-[#EBE9E4] bg-[#FBFAF6] p-3.5 flex items-start gap-2.5">
            <Lock size={15} strokeWidth={1.6} className="text-[#6B6B6B] mt-0.5 flex-shrink-0" />
            <p className="text-[13px] leading-relaxed text-[#474747]">
              Plus gives you an early heads-up when your recent check-ins start trending toward a
              harder stretch — and reminds you what helped you last time.
            </p>
          </div>
          <button
            data-testid="radar-upgrade"
            onClick={() => nav("/plus")}
            className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-[#1A1A1A] text-[#FDFBF7] rounded-full px-5 py-3 text-[14px] font-medium ss-tap ss-focus hover:-translate-y-0.5"
          >
            <Sparkles size={15} strokeWidth={1.8} /> See it with Plus
          </button>
        </>
      ) : (
        <>
          <p className="mt-3 text-[14.5px] leading-relaxed text-[#1A1A1A]" data-testid="radar-observation">
            {r.observation}
          </p>
          {r.helped_before && (
            <div className="mt-3 rounded-xl border border-[#EBE9E4] bg-[#FBFAF6] p-3.5">
              <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#8a6d10]">
                What helped you before
              </div>
              <p className="mt-1 text-[13.5px] leading-relaxed text-[#474747]">
                Last time this pattern appeared, “{r.helped_before}” seemed to help. It might be worth
                trying again.
              </p>
            </div>
          )}
          <p className="mt-3 text-[12px] leading-relaxed text-[#9a9a94]">{r.reassurance}</p>
        </>
      )}
    </motion.div>
  );
}
